import React, { useCallback, useContext, useEffect, useState } from "react";
import { AllModules, ColDef } from "@ag-grid-enterprise/all-modules";

import { AgGridReact } from "@ag-grid-community/react";

import { Alert } from "@blueprintjs/core";
import { DELETE_SCHEDULE } from "graphql/mutations";
import { GET_SCHEDULES } from "graphql/queries";
import { useSchedules } from "hooks/useSchedules";
import { useTemplateGridOptions } from "hooks/useTemplateGridOptions";
import { ScheduleDoc } from "types";
import { SelectedProjectContext } from "utils/context";
import { getZonedTime, scheduleKey } from "utils/utils";
import { useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { ScheduleDeleteToaster } from "utils/toasters";

const dateComparator = (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime();

type Props = {
    setSchedule: (s: ScheduleDoc | undefined) => void;
    setEditing: (f: boolean) => void;
    setCloning: (f: boolean) => void;
    toggle: () => void;
};

export default function ScheduleGrid({ setSchedule, setCloning, setEditing, toggle }: Props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const getSearchParam = () => {
        return searchParams.get(scheduleKey);
    };

    const [gridOptions, gridApi] = useTemplateGridOptions(columnDefs, undefined, getSearchParam, true);
    const [selectedProject] = useContext(SelectedProjectContext);
    const [schedules, loading, error] = useSchedules(selectedProject?.projectId);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [delSchedule] = useMutation(DELETE_SCHEDULE);
    const [scheduleToDelete, setScheduleToDelete] = useState<ScheduleDoc | undefined>();
    const [deleting, setDeleting] = useState(false);

    const handleDeleteConfirm = useCallback(async () => {
        if (!scheduleToDelete) {
            return;
        }
        const { company, id } = { ...scheduleToDelete };
        setDeleting(true);
        await delSchedule({
            variables: { companyId: company.sysId, scheduleId: id },
            //todo: figure out caching
            refetchQueries: [
                {
                    query: GET_SCHEDULES,
                    variables: { projectId: selectedProject?.projectId }
                }
            ]
        })
            .then((res) => {
                ScheduleDeleteToaster.show({
                    message: `Successfully Deleted Schedule.`,
                    intent: "warning"
                });
            })
            .catch((err) => {
                ScheduleDeleteToaster.show({
                    message: `Failed to Delete the Schedule.`,
                    intent: "danger"
                });
            })
            .finally(() => {
                setDeleteOpen(false);
                setDeleting(false);
            });
    }, [delSchedule, scheduleToDelete, selectedProject]);

    useEffect(() => {
        if (gridApi) {
            if (loading) {
                gridApi.showLoadingOverlay();
            }

            if (!loading && schedules) {
                gridApi.hideOverlay();
                gridApi.setRowData(schedules);
            }

            //todo: figure out how to show error overlay
            // if (!error) {
            //     gridOptions.overlayNoRowsTemplate =
            //         '<span class="messageStyles">Problem with service link. Please try again later</span>';
            //     gridApi.showNoRowsOverlay();
            // }
        }
    }, [error, gridApi, gridOptions, loading, schedules]);

    useEffect(() => {
        if (gridApi && gridOptions) {
            gridOptions.onRowDataUpdated = (e) => {
                const selectedNodes = e.api.getSelectedNodes();
                // when a schedule is updated this will makes sure that the most recent data is
                // shown by forcing ag-grid to reselect the schedule
                if (selectedNodes && selectedNodes[0]) {
                    setSchedule(selectedNodes[0].data);
                    // this will ensure that when active changes the color of the cell will change
                    gridApi.redrawRows();
                } else {
                    setSchedule(undefined);
                }
            };
            gridOptions.onSelectionChanged = (e) => {
                const selectedNodes = e.api.getSelectedNodes();
                if (selectedNodes && selectedNodes[0]) {
                    searchParams.set(scheduleKey, btoa(selectedNodes[0].id));
                    setSearchParams(searchParams);
                    setSchedule(selectedNodes[0].data);
                }
            };
        }
    }, [gridApi, gridOptions, searchParams, setSchedule, setSearchParams]);

    useEffect(() => {
        if (gridOptions) {
            const getContextMenuItems = (params: any) => {
                params.node.setSelected(true);
                const schedule = params.node.data as ScheduleDoc;

                return [
                    {
                        name: "Clone",
                        action: () => {
                            setCloning(true);
                            toggle();
                        },
                        icon: "<span class='bp3-icon-standard bp3-icon-duplicate bp3-intent-primary'></span>"
                    },

                    {
                        name: "Edit",
                        action: () => {
                            setEditing(true);
                            toggle();
                        },
                        icon: "<span class='bp3-icon-standard bp3-icon-edit bp3-intent-primary'></span>"
                    },
                    // {
                    //     name: "Copy Link",
                    //     action: () => {
                    //         navigator.clipboard.writeText(window.location.href);
                    //         CopyToaster.show({
                    //             message: `Copied to Clipboard`,
                    //             intent: "success",
                    //             timeout: 1500
                    //         });
                    //     },
                    //     icon: "<span class='bp3-icon-standard bp3-icon-link bp3-intent-primary'></span>"
                    // },
                    "separator",
                    {
                        name: "Delete",
                        action: () => {
                            setScheduleToDelete(schedule);
                            setDeleteOpen(true);

                            // toggleDeleteConfrim();
                            // setScheduleToDelete(clickedSchedule);
                            // deleteSchedule(
                            //     clickedSchedule.company.sysId,
                            //     clickedSchedule.id,
                            //     selectedProjectRef.current?.projectId
                            // );
                        },

                        icon: "<span class='bp3-icon-standard bp3-icon-trash bp3-intent-danger'></span>"
                    }

                    // {
                    //     name: "Delete",
                    //     action: () => {
                    //         const node: AvailableTemplate = params.node.data;
                    //         setSelectedTemplate(node);
                    //         setConfirmOpen(true);
                    //     },
                    //     icon: "<span class='ag-icon ag-icon-cross'></span>"
                    // }
                ];
            };
            gridOptions.getContextMenuItems = getContextMenuItems;
        }
    }, [gridOptions, setCloning, setEditing, setSchedule, toggle]);

    return (
        <>
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Confirm"
                icon="trash"
                intent="danger"
                isOpen={deleteOpen}
                loading={deleting}
                onCancel={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
            >
                <p>
                    Are you sure you want to delete this schedule? <b>This cannot be undone!</b>
                </p>
            </Alert>
            <AgGridReact modules={AllModules} gridOptions={gridOptions} />
        </>
    );
}

const columnDefs: ColDef[] = [
    {
        colId: "id",
        headerName: "Id",
        field: "id",
        cellClass: "no-border",
        hide: true
    },
    {
        colId: "company",
        headerName: "Company",
        field: "company.name",
        cellClass: "no-border"
        // pinned: "left",
        // cellRenderer: "companyCellRenderer"
    },
    {
        colId: "name",
        headerName: "Name",
        field: "schedule.schedName",
        cellClass: "no-border"
    },

    {
        colId: "active",
        headerName: "Active",
        field: "active",
        cellClass: "no-border",
        cellStyle: (params) => {
            return params.value ? null : { color: "#F5F8FA", backgroundColor: "#D9822B" };
        }
    },
    {
        colId: "lastRuntime",
        headerName: "Last Run Time",
        field: "lastRuntime",
        cellClass: "no-border",
        sort: "desc",
        //todo: add date filtering
        menuTabs: [],
        comparator: dateComparator,
        valueFormatter: ({ data }) => {
            return data.lastRuntime
                ? getZonedTime(data.lastRuntime, Intl.DateTimeFormat().resolvedOptions().timeZone)
                : "";
        }

        // cellRenderer: "companyCellRenderer"
    },
    {
        colId: "nextRunTime",
        headerName: "Next Run Time",
        //todo: add date filtering
        menuTabs: [],
        field: "nextRunTime",
        cellClass: "no-border",
        valueFormatter: ({ data }) => getZonedTime(data.nextRunTime, Intl.DateTimeFormat().resolvedOptions().timeZone),
        flex: 1
    }
];
// {
//     colId: "updateOn",
//     headerName: "Last Updated",
//     field: "updatedOn",
//     valueGetter: ({ data }) => new Date(data.updatedOn),
//     valueFormatter: ({ data }) => relativeTime(data.updatedOn),
//     tooltipValueGetter: ({ value }) => localTime(value),
//     cellClass: "no-border",
//     filter: ""
// },
// {
//     colId: "createdOn",
//     headerName: "Created On",
//     field: "createdOn",
//     valueFormatter: ({ data }) => localTime(data.createdOn),
//     cellClass: "no-border"
// },
